import { z } from 'zod';
import { apiFetch } from './client.js';
import type { CatalogItem, CatalogResponse } from './types.js';

// ---------------------------------------------------------------------------
// Wire-layer Zod schemas (snake_case)
// ---------------------------------------------------------------------------

const LastExecutionWireSchema = z.object({
  executed_at: z.string(),
  duration_ms: z.number().nullable(),
  row_count: z.number().nullable(),
});

const ReportCapabilitiesWireSchema = z.object({
  level: z.enum(['view', 'execute', 'full']),
  can_export: z.boolean(),
  can_save_personal_view: z.boolean(),
  can_save_shared_view: z.boolean(),
  can_drilldown: z.boolean(),
});

const CatalogItemWireSchema = z.object({
  tenant_report_id: z.string(),
  code: z.string(),
  version: z.number(),
  kind: z.enum(['report', 'dashboard']),
  category: z.string(),
  title: z.record(z.string()),
  description: z.record(z.string()).optional().nullable(),
  icon: z.string().optional().nullable(),
  is_favorite: z.boolean(),
  capabilities: ReportCapabilitiesWireSchema,
  last_execution: LastExecutionWireSchema.optional().nullable(),
  version_status: z.enum(['current', 'update_available']),
});

const PaginationWireSchema = z.object({
  page_number: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
  total_items: z.number(),
});

const CatalogResponseWireSchema = z.object({
  items: z.array(CatalogItemWireSchema),
  pagination: PaginationWireSchema,
});

// ---------------------------------------------------------------------------
// Query params
// ---------------------------------------------------------------------------

export interface CatalogQuery {
  kind?: 'report' | 'dashboard';
  category?: string;
  q?: string;
  favoritesOnly?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// Mapper: wire → CatalogItem (already matches shared types.ts, minor coercion)
// ---------------------------------------------------------------------------

function mapWireItem(wire: z.infer<typeof CatalogItemWireSchema>): CatalogItem {
  const item: CatalogItem = {
    tenant_report_id: wire.tenant_report_id,
    code: wire.code,
    version: wire.version,
    kind: wire.kind,
    category: wire.category,
    title: wire.title,
    is_favorite: wire.is_favorite,
    capabilities: {
      level: wire.capabilities.level === 'full' ? 'manage' : wire.capabilities.level,
      can_export: wire.capabilities.can_export,
      can_save_personal_view: wire.capabilities.can_save_personal_view,
      can_save_shared_view: wire.capabilities.can_save_shared_view,
      can_drilldown: wire.capabilities.can_drilldown,
    },
    version_status: wire.version_status,
  };

  if (wire.description != null) {
    item.description = wire.description;
  }

  if (wire.last_execution != null) {
    item.last_execution = {
      executed_at: wire.last_execution.executed_at,
      duration_ms: wire.last_execution.duration_ms ?? 0,
      row_count: wire.last_execution.row_count ?? 0,
    };
  }

  return item;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function getCatalog(params: CatalogQuery): Promise<CatalogResponse> {
  const searchParams = new URLSearchParams();

  if (params.kind !== undefined) searchParams.set('kind', params.kind);
  if (params.category !== undefined && params.category.length > 0)
    searchParams.set('category', params.category);
  if (params.q !== undefined && params.q.length > 0) searchParams.set('q', params.q);
  if (params.favoritesOnly === true) searchParams.set('favorites_only', 'true');
  if (params.pageNumber !== undefined) searchParams.set('page-number', String(params.pageNumber));
  if (params.pageSize !== undefined) searchParams.set('page-size', String(params.pageSize));

  const query = searchParams.toString();
  const path = query.length > 0 ? `/reports/catalog?${query}` : '/reports/catalog';

  const raw = await apiFetch<unknown>(path);
  const parsed = CatalogResponseWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Catalog response shape mismatch: ${parsed.error.message}`);
  }

  return {
    items: parsed.data.items.map(mapWireItem),
    pagination: parsed.data.pagination,
  };
}

export async function setFavorite(
  tenantReportId: string,
  favorite: boolean,
): Promise<{ is_favorite: boolean }> {
  const method = favorite ? 'PUT' : 'DELETE';
  await apiFetch<undefined>(`/reports/${tenantReportId}/favorite`, { method });
  return { is_favorite: favorite };
}

// ---------------------------------------------------------------------------
// F1. Report detail
// ---------------------------------------------------------------------------

const SavedViewWireSchema = z.object({
  id: z.string(),
  name: z.string(),
  scope: z.enum(['personal', 'shared']),
  state: z.record(z.unknown()),
  created_at: z.string(),
  updated_at: z.string(),
});

const ReportDetailWireSchema = z.object({
  tenant_report_id: z.string(),
  code: z.string(),
  version: z.number(),
  kind: z.enum(['report', 'dashboard']),
  definition: z.record(z.unknown()),
  overrides: z.record(z.unknown()).optional().nullable(),
  capabilities: ReportCapabilitiesWireSchema,
  is_favorite: z.boolean(),
  saved_views: z.array(SavedViewWireSchema),
  version_status: z.enum(['current', 'update_available']),
  available_versions: z.array(z.number()).optional(),
  last_execution: LastExecutionWireSchema.optional().nullable(),
});

export interface ReportDetail {
  tenantReportId: string;
  code: string;
  version: number;
  kind: 'report' | 'dashboard';
  definition: Record<string, unknown>;
  overrides?: Record<string, unknown>;
  capabilities: {
    level: 'view' | 'execute' | 'manage';
    canExport: boolean;
    canSavePersonalView: boolean;
    canSaveSharedView: boolean;
    canDrilldown: boolean;
  };
  isFavorite: boolean;
  savedViews: Array<{
    id: string;
    name: string;
    scope: 'personal' | 'shared';
    state: Record<string, unknown>;
    createdAt: string;
    updatedAt: string;
  }>;
  versionStatus: 'current' | 'update_available';
  availableVersions?: number[];
  lastExecution?: { executedAt: string; durationMs: number; rowCount: number };
}

function mapReportDetail(wire: z.infer<typeof ReportDetailWireSchema>): ReportDetail {
  const detail: ReportDetail = {
    tenantReportId: wire.tenant_report_id,
    code: wire.code,
    version: wire.version,
    kind: wire.kind,
    definition: wire.definition,
    capabilities: {
      level: wire.capabilities.level === 'full' ? 'manage' : wire.capabilities.level,
      canExport: wire.capabilities.can_export,
      canSavePersonalView: wire.capabilities.can_save_personal_view,
      canSaveSharedView: wire.capabilities.can_save_shared_view,
      canDrilldown: wire.capabilities.can_drilldown,
    },
    isFavorite: wire.is_favorite,
    savedViews: wire.saved_views.map((sv) => ({
      id: sv.id,
      name: sv.name,
      scope: sv.scope,
      state: sv.state,
      createdAt: sv.created_at,
      updatedAt: sv.updated_at,
    })),
    versionStatus: wire.version_status,
  };

  if (wire.overrides != null) detail.overrides = wire.overrides;
  if (wire.available_versions !== undefined) detail.availableVersions = wire.available_versions;
  if (wire.last_execution != null) {
    detail.lastExecution = {
      executedAt: wire.last_execution.executed_at,
      durationMs: wire.last_execution.duration_ms ?? 0,
      rowCount: wire.last_execution.row_count ?? 0,
    };
  }

  return detail;
}

export async function getReportDetail(tenantReportId: string): Promise<ReportDetail> {
  const raw = await apiFetch<unknown>(`/reports/${tenantReportId}`);
  const parsed = ReportDetailWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Report detail response shape mismatch: ${parsed.error.message}`);
  }
  return mapReportDetail(parsed.data);
}

// ---------------------------------------------------------------------------
// F1. Execute
// ---------------------------------------------------------------------------

const ExecutionMetaWireSchema = z.object({
  execution_id: z.string(),
  executed_at: z.string(),
  duration_ms: z.number(),
  cache: z.object({ hit: z.boolean() }),
});

const ExecuteResponseWireSchema = z.object({
  rows: z.array(z.record(z.unknown())),
  total_rows: z.number(),
  total_filtered_rows: z.number().optional(),
  schema: z.array(z.record(z.unknown())).optional(),
  pagination: z
    .object({
      page_number: z.number(),
      page_size: z.number(),
      total_pages: z.number(),
    })
    .optional(),
  meta: ExecutionMetaWireSchema,
  truncated: z.boolean().optional(),
  aggregates: z.record(z.unknown()).optional(),
  applied_query: z.string().optional().nullable(),
});

export interface ExecuteRequest {
  params: Record<string, unknown>;
  view_state?: Record<string, unknown>;
  fresh?: boolean;
}

export interface ExecuteResponse {
  rows: ReadonlyArray<Record<string, unknown>>;
  totalRows: number;
  totalFilteredRows: number;
  pagination: { pageNumber: number; pageSize: number; totalPages: number } | undefined;
  meta: { executionId: string; executedAt: string; durationMs: number; cacheHit: boolean };
  truncated: boolean;
  aggregates: Record<string, unknown> | undefined;
  appliedQuery: string | undefined;
}

export async function executeReport(
  tenantReportId: string,
  body: ExecuteRequest,
): Promise<ExecuteResponse> {
  const raw = await apiFetch<unknown>(`/reports/${tenantReportId}/execute`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const parsed = ExecuteResponseWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Execute response shape mismatch: ${parsed.error.message}`);
  }
  const d = parsed.data;
  return {
    rows: d.rows,
    totalRows: d.total_rows,
    totalFilteredRows: d.total_filtered_rows ?? d.total_rows,
    pagination: d.pagination
      ? {
          pageNumber: d.pagination.page_number,
          pageSize: d.pagination.page_size,
          totalPages: d.pagination.total_pages,
        }
      : undefined,
    meta: {
      executionId: d.meta.execution_id,
      executedAt: d.meta.executed_at,
      durationMs: d.meta.duration_ms,
      cacheHit: d.meta.cache.hit,
    },
    truncated: d.truncated ?? false,
    aggregates: d.aggregates,
    appliedQuery: d.applied_query ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// F1. Export lifecycle
// ---------------------------------------------------------------------------

const ExportSummaryWireSchema = z.object({
  export_id: z.string(),
  tenant_report_id: z.string().optional().nullable(),
  format: z.enum(['csv', 'xlsx', 'pdf']),
  filename: z.string(),
  status: z.enum(['queued', 'running', 'completed', 'failed', 'cancelled']),
  progress: z.number().optional(),
  row_count: z.number().optional().nullable(),
  size_bytes: z.number().optional().nullable(),
  requested_at: z.string().optional(),
  started_at: z.string().optional().nullable(),
  completed_at: z.string().optional().nullable(),
  expires_at: z.string().optional().nullable(),
  download_url: z.string().optional().nullable(),
  estimated_seconds: z.number().optional(),
  poll_url: z.string().optional(),
  error: z.object({ code: z.string(), message: z.string() }).nullable().optional(),
});

export type ExportStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface ExportSummary {
  exportId: string;
  tenantReportId?: string;
  format: 'csv' | 'xlsx' | 'pdf';
  filename: string;
  status: ExportStatus;
  progress?: number;
  rowCount?: number;
  sizeBytes?: number;
  requestedAt?: string;
  startedAt?: string;
  completedAt?: string;
  expiresAt?: string;
  downloadUrl?: string;
  estimatedSeconds?: number;
  error?: { code: string; message: string };
}

function mapExportSummary(wire: z.infer<typeof ExportSummaryWireSchema>): ExportSummary {
  const result: ExportSummary = {
    exportId: wire.export_id,
    format: wire.format,
    status: wire.status,
    filename: wire.filename,
  };
  if (wire.tenant_report_id != null) result.tenantReportId = wire.tenant_report_id;
  if (wire.progress !== undefined) result.progress = wire.progress;
  if (wire.row_count != null) result.rowCount = wire.row_count;
  if (wire.size_bytes != null) result.sizeBytes = wire.size_bytes;
  if (wire.requested_at !== undefined) result.requestedAt = wire.requested_at;
  if (wire.started_at != null) result.startedAt = wire.started_at;
  if (wire.completed_at != null) result.completedAt = wire.completed_at;
  if (wire.expires_at != null) result.expiresAt = wire.expires_at;
  if (wire.download_url != null) result.downloadUrl = wire.download_url;
  if (wire.estimated_seconds !== undefined) result.estimatedSeconds = wire.estimated_seconds;
  if (wire.error != null) result.error = wire.error;
  return result;
}

export interface ExportRequest {
  params: Record<string, unknown>;
  view_state?: Record<string, unknown>;
  format: 'csv' | 'xlsx';
  filename_hint?: string;
}

export async function requestExport(
  tenantReportId: string,
  body: ExportRequest,
  idempotencyKey?: string,
): Promise<ExportSummary> {
  const headers: Record<string, string> = {};
  if (idempotencyKey !== undefined) headers['Idempotency-Key'] = idempotencyKey;

  const raw = await apiFetch<unknown>(`/reports/${tenantReportId}/export`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers,
  });
  const parsed = ExportSummaryWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Export response shape mismatch: ${parsed.error.message}`);
  }
  return mapExportSummary(parsed.data);
}

export async function getExportStatus(exportId: string): Promise<ExportSummary> {
  const raw = await apiFetch<unknown>(`/reports/exports/${exportId}`);
  const parsed = ExportSummaryWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Export status response shape mismatch: ${parsed.error.message}`);
  }
  return mapExportSummary(parsed.data);
}

const DownloadUrlResponseSchema = z.object({
  download_url: z.string(),
  expires_in_seconds: z.number(),
});

export interface DownloadUrlResponse {
  downloadUrl: string;
  expiresInSeconds: number;
}

export async function getExportDownloadUrl(exportId: string): Promise<DownloadUrlResponse> {
  // B-1: request JSON envelope so the signed URL is returned with the Authorization
  // header in-flight (apiFetch adds Bearer token). Browser navigation to /download
  // would lose the auth header and receive 401.
  const raw = await apiFetch<unknown>(`/reports/exports/${exportId}/download`, {
    headers: { Accept: 'application/json' },
  });
  const parsed = DownloadUrlResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Download URL response shape mismatch: ${parsed.error.message}`);
  }
  return {
    downloadUrl: parsed.data.download_url,
    expiresInSeconds: parsed.data.expires_in_seconds,
  };
}

export async function cancelExport(exportId: string): Promise<void> {
  await apiFetch<undefined>(`/reports/exports/${exportId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// F1. List exports (§8.5)
// ---------------------------------------------------------------------------

const ExportListResponseWireSchema = z.object({
  items: z.array(ExportSummaryWireSchema),
  pagination: PaginationWireSchema,
});

export interface ExportListParams {
  tenant_report_id?: string;
  status?: ExportStatus;
  format?: 'csv' | 'xlsx' | 'pdf';
  page_number?: number;
  page_size?: number;
}

export interface ExportListResponse {
  items: ExportSummary[];
  pagination: { page_number: number; page_size: number; total_pages: number; total_items: number };
}

export async function listExports(params: ExportListParams = {}): Promise<ExportListResponse> {
  const sp = new URLSearchParams();
  if (params.tenant_report_id !== undefined) sp.set('tenant_report_id', params.tenant_report_id);
  if (params.status !== undefined) sp.set('status', params.status);
  if (params.format !== undefined) sp.set('format', params.format);
  if (params.page_number !== undefined) sp.set('page-number', String(params.page_number));
  if (params.page_size !== undefined) sp.set('page-size', String(params.page_size));

  const query = sp.toString();
  const path = query.length > 0 ? `/reports/exports?${query}` : '/reports/exports';

  const raw = await apiFetch<unknown>(path);
  const parsed = ExportListResponseWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Export list response shape mismatch: ${parsed.error.message}`);
  }

  return {
    items: parsed.data.items.map(mapExportSummary),
    pagination: parsed.data.pagination,
  };
}

// ---------------------------------------------------------------------------
// F1. Refs lookup / resolve
// ---------------------------------------------------------------------------

const RefItemWireSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const LookupResponseWireSchema = z.object({
  items: z.array(RefItemWireSchema),
  pagination: PaginationWireSchema,
});

export interface LookupQuery {
  view: string;
  labelField: string;
  valueField: string;
  q?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface RefItem {
  value: string;
  label: string;
}

export interface LookupResponse {
  items: RefItem[];
  pagination: { pageNumber: number; pageSize: number; totalPages: number; totalItems: number };
}

export async function lookupRef(query: LookupQuery, signal?: AbortSignal): Promise<LookupResponse> {
  const sp = new URLSearchParams();
  sp.set('view', query.view);
  sp.set('label_field', query.labelField);
  sp.set('value_field', query.valueField);
  if (query.q !== undefined && query.q.length > 0) sp.set('q', query.q);
  if (query.pageNumber !== undefined) sp.set('page-number', String(query.pageNumber));
  if (query.pageSize !== undefined) sp.set('page-size', String(query.pageSize));

  const raw = await apiFetch<unknown>(`/refs/lookup?${sp.toString()}`, { signal: signal ?? null });
  const parsed = LookupResponseWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Lookup response shape mismatch: ${parsed.error.message}`);
  }
  return {
    items: parsed.data.items,
    pagination: {
      pageNumber: parsed.data.pagination.page_number,
      pageSize: parsed.data.pagination.page_size,
      totalPages: parsed.data.pagination.total_pages,
      totalItems: parsed.data.pagination.total_items,
    },
  };
}

const ResolveResponseWireSchema = z.object({
  items: z.array(RefItemWireSchema),
});

export interface ResolveQuery {
  view: string;
  valueField: string;
  values: string[];
}

export interface ResolveResponse {
  items: RefItem[];
}

export async function resolveRef(query: ResolveQuery): Promise<ResolveResponse> {
  const sp = new URLSearchParams();
  sp.set('view', query.view);
  sp.set('value_field', query.valueField);
  sp.set('values', query.values.join(','));

  const raw = await apiFetch<unknown>(`/refs/resolve?${sp.toString()}`);
  const parsed = ResolveResponseWireSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(`Resolve response shape mismatch: ${parsed.error.message}`);
  }
  return { items: parsed.data.items };
}
